import { can } from "../../auth/can.ts";
import React from "react";
import { Partner } from "../../models/Partner.ts";
import { useSelector } from "react-redux";
import { RootState } from "../../store/index.ts";
import ApplicationListReinstatements from "../history/ApplicationListReinstatements.tsx";
import ApplicationListChangePosition from "../history/ApplicationListChangePosition.tsx";
import ApplicationListChangeBranch from "../history/ApplicationListChangeBranch.tsx";
import ApplicationListChangeSalary from "../history/ApplicationListChangeSalary.tsx";
import {
  getChangePositionByPartner,
  readApplicationsByCurp,
} from "../../services/application.service.ts";
import { getSalaryHistory } from "../../services/partner.service.ts";
import { getProfileTabPaneClass } from "./profileTabUtils.ts";

const TAB_CONFIG = [
  { id: 0, label: "Reintegraciones", icon: "fa-rotate-left" },
  { id: 1, label: "Cambios de posición", icon: "fa-briefcase" },
  { id: 2, label: "Cambios de sucursal", icon: "fa-building" },
  { id: 3, label: "Cambios de salario", icon: "fa-coins" },
] as const;

const styles: { [key: string]: React.CSSProperties } = {
  wrapper: {
    display: "flex",
    width: "100%",
    margin: 0,
    padding: 0,
    minHeight: "100%",
    height: "100%",
    backgroundColor: "#fff",
    borderRadius: "12px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
    border: "1px solid #e5e7eb",
    overflow: "hidden",
  },
  sidebar: {
    flexShrink: 0,
    width: "220px",
    backgroundColor: "#f1f5f9",
    borderRight: "1px solid #e2e8f0",
    padding: "12px 8px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  tab: {
    padding: "12px 14px",
    fontSize: "13px",
    fontWeight: "600",
    color: "#64748b",
    backgroundColor: "transparent",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    textAlign: "left",
    width: "100%",
  },
  tabActive: {
    color: "#fff",
    backgroundColor: "#64748b",
    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
  },
  content: {
    flex: 1,
    minWidth: 0,
    padding: "20px 24px",
    backgroundColor: "#fafafa",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "auto",
  },
  panel: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  panelInner: {
    width: "100%",
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  },
};

const Movimientos = ({
  partner,
  reloadPage,
  isTabActive = false,
  embedded = false,
}: {
  partner: Partner;
  reloadPage: () => Promise<void>;
  isTabActive?: boolean;
  embedded?: boolean;
}) => {
  const { user } = useSelector((state: RootState) => state.auth);
  const curp = partner?.person?.curp;
  const [value, setValue] = React.useState(0);
  const [applications, setApplications] = React.useState<any[]>([]);
  const [changeBranch, setChangeBranch] = React.useState<any[]>([]);
  const [changeSalary, setChangeSalary] = React.useState<any[]>([]);

  const fetchData = React.useCallback(async () => {
    if (!curp || !partner?.id) return;
    try {
      const [apps, branches, salaries] = await Promise.all([
        readApplicationsByCurp(curp),
        getChangePositionByPartner(partner.id),
        getSalaryHistory(partner.id),
      ]);
      setApplications(apps || []);
      setChangeBranch(branches || []);
      setChangeSalary(salaries || []);
    } catch (err) {
      console.error("Error loading movimientos:", err);
    }
  }, [curp, partner?.id]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const showSalaryTab = Boolean(
    can("partners.view_salary", user)
  );

  const tabs = TAB_CONFIG.filter((t) => t.id !== 3 || showSalaryTab);

  return (
    <div
      className={`${getProfileTabPaneClass(isTabActive, embedded)} movimientos-tab-pane`}
      id="movimientos"
      style={{
        margin: embedded ? 0 : "0 -13px",
        padding: embedded ? 0 : "0 -10px",
        minHeight: embedded ? "auto" : "calc(100vh - 200px)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ ...styles.wrapper, minHeight: "calc(100vh - 200px)" }}>
        <div
          style={styles.sidebar}
          role="tablist"
          aria-label="Movimientos"
        >
          {tabs.map((tab) => {
            const isActive = value === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-controls={`movimientos-panel-${tab.id}`}
                id={`movimientos-tab-${tab.id}`}
                style={{
                  ...styles.tab,
                  ...(isActive ? styles.tabActive : {}),
                }}
                onClick={() => setValue(tab.id)}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = "#f1f5f9";
                    e.currentTarget.style.color = "#475569";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "#64748b";
                  }
                }}
              >
                <i className={`fa-solid ${tab.icon}`} style={{ fontSize: "12px" }} />
                {tab.label}
              </button>
            );
          })}
        </div>
        <div style={styles.content}>
          <div
            role="tabpanel"
            id="movimientos-panel-0"
            aria-labelledby="movimientos-tab-0"
            hidden={value !== 0}
            style={{ ...styles.panel, display: value !== 0 ? "none" : "flex" }}
          >
            {value === 0 && (
              <div className="movimientos-content-full" style={styles.panelInner}>
                <ApplicationListReinstatements
                  applications={applications}
                  size="sm"
                />
              </div>
            )}
          </div>
          <div
            role="tabpanel"
            id="movimientos-panel-1"
            aria-labelledby="movimientos-tab-1"
            hidden={value !== 1}
            style={{ ...styles.panel, display: value !== 1 ? "none" : "flex" }}
          >
            {value === 1 && (
              <div className="movimientos-content-full" style={styles.panelInner}>
                <ApplicationListChangePosition
                  applications={applications}
                  size="sm"
                />
              </div>
            )}
          </div>
          <div
            role="tabpanel"
            id="movimientos-panel-2"
            aria-labelledby="movimientos-tab-2"
            hidden={value !== 2}
            style={{ ...styles.panel, display: value !== 2 ? "none" : "flex" }}
          >
            {value === 2 && (
              <div className="movimientos-content-full" style={styles.panelInner}>
                <ApplicationListChangeBranch
                  applications={changeBranch}
                  size="sm"
                />
              </div>
            )}
          </div>
          {showSalaryTab && (
            <div
              role="tabpanel"
              id="movimientos-panel-3"
              aria-labelledby="movimientos-tab-3"
              hidden={value !== 3}
              style={{ ...styles.panel, display: value !== 3 ? "none" : "flex" }}
            >
              {value === 3 && (
                <div className="movimientos-content-full" style={styles.panelInner}>
                  <ApplicationListChangeSalary
                    applications={changeSalary}
                    size="sm"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Movimientos;
